import { useState } from 'react';
import { useAccessToken } from '../auth/use-access-token.js';
import { makeClientLink } from '../api/coach-clients.js';
import { BusyLabel } from './busy-label.js';

/**
 * Share link (mockup *Clients and Texted Links* v8, screen 5, approved 21
 * October): a fresh sign-up link, handed to the phone's own share sheet with
 * the message ready, for the coach to text from their own phone. The portal
 * sends nothing. Each share makes a new link and the old one stops working.
 *
 * The message is Matthew's words (ruled 21 October, made grammatical on his
 * say), the four value ticks, and "Tap to sign up". Where there is no share
 * sheet - a computer - the message is copied instead, and the button says so.
 */
export function shareMessage(coachName: string, link: string): string {
  return [
    `Coach ${coachName} created your family coaching profile. Complete it and let's get to hitting different!`,
    '✓ Book online',
    '✓ Lesson notes and videos',
    '✓ Message your coach',
    '✓ Practice tools',
    `Tap to sign up: ${link}`,
  ].join('\n');
}

export function ShareFamilyLink({ adultAccountId }: { adultAccountId: string }) {
  const getToken = useAccessToken();
  const [state, setState] = useState<'idle' | 'making' | 'copied' | 'failed'>('idle');
  // A message made but not yet handed over: Safari may refuse the share sheet
  // when making the link took too long after the tap. The next tap shares it
  // at once, without making another.
  const [waiting, setWaiting] = useState<string | null>(null);

  async function hand(text: string) {
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ text });
        setWaiting(null);
      } catch (cause) {
        // Not allowed: kept for the next tap. Closed without sending: done; sharing again makes a new link.
        setWaiting(cause instanceof DOMException && cause.name === 'NotAllowedError' ? text : null);
      }
      setState('idle');
      return;
    }
    await navigator.clipboard.writeText(text);
    setWaiting(null);
    setState('copied');
  }

  async function share() {
    if (state === 'making') return;
    try {
      if (waiting) {
        await hand(waiting);
        return;
      }
      setState('making');
      const made = await makeClientLink(getToken, adultAccountId);
      await hand(shareMessage(made.coach_name, `${window.location.origin}${made.path}`));
    } catch {
      setState('failed');
    }
  }

  return (
    <>
      <button type="button" className="primary" onClick={() => void share()} disabled={state === 'making'} aria-busy={state === 'making'}>
        <BusyLabel busy={state === 'making'} working="Making the link…">
          {state === 'copied' ? 'Link copied' : 'Share link'}
        </BusyLabel>
      </button>
      {state === 'failed' ? <p className="form-note">The link was not made. Try again.</p> : null}
    </>
  );
}
